import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import LabeledInput from '../LabeledInput';
import { DatePicker } from '../DatePicker';
import Enum from '../Question/Enum';
import { TextInput } from '../TextInput';

import { isEnumMember } from 'common/dist/utils/types';
import * as api from '../../api';
import { EMPLOYMENT_STATUS_SHOW_EMPLOYER } from 'common/dist/utils/previsitFormField';

import styles from './index.module.css';

interface EmploymentData {
  retirementDate: Date | null;
  employmentStatus: api.PracticePreference.EmploymentStatus | null;
  employer: string | null;
}

interface Props extends EmploymentData {
  employmentStatusOptions: api.PracticePreference.EmploymentStatus[];
  onChange(values: Partial<EmploymentData>): void;
  onSave(values: Partial<EmploymentData>): void;
  collectRetirementDate?: boolean;
}

const EmploymentInput: React.FC<Props> = ({
  collectRetirementDate,
  employmentStatus,
  employmentStatusOptions,
  employer,
  retirementDate,
  onChange,
  onSave,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <LabeledInput label={t('Employment status')} htmlFor="employment-status">
        <Enum
          listClassName={classNames(styles.Enum, styles.EmploymentEnum)}
          onChange={(employmentStatus: string) => {
            if (
              !isEnumMember(
                api.PracticePreference.EmploymentStatus,
                employmentStatus,
              )
            ) {
              return;
            }
            const updateValues = {
              employmentStatus,
              employmentStatusRaw: employmentStatus,
              ...(!EMPLOYMENT_STATUS_SHOW_EMPLOYER.includes(
                employmentStatus,
              ) && { employer: null }),
            };
            onSave(updateValues);
          }}
          options={employmentStatusOptions.map((status) => ({
            display: t(api.Previsit.employmentStatusOptionsMap[status]),
            key: status,
            value: status,
          }))}
          value={employmentStatus?.toString() || ''}
        />
      </LabeledInput>

      {employmentStatus &&
        EMPLOYMENT_STATUS_SHOW_EMPLOYER.includes(employmentStatus) && (
          <LabeledInput label={t('Employer')} htmlFor="employer">
            <TextInput
              id="employer"
              onChange={(e) =>
                onChange({ employer: e.target.value, retirementDate: null })
              }
              onBlur={() => onSave({ employer })}
              value={employer || ''}
            />
          </LabeledInput>
        )}

      {collectRetirementDate &&
        employmentStatus ===
          api.PracticePreference.EmploymentStatus.Retired && (
          <LabeledInput
            label="Retirement date"
            htmlFor="guarantor-retirement-date"
          >
            <DatePicker
              curvedInput={5}
              date={
                retirementDate && !isNaN(new Date(retirementDate).valueOf())
                  ? new Date(retirementDate).toISOString()
                  : ''
              }
              onChange={(date) => {
                onChange({ retirementDate: date });
              }}
            />
          </LabeledInput>
        )}
    </>
  );
};

export default EmploymentInput;
